import React, { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import type { User } from '../../services/authService';

/**
 * Hard onboarding gate for job-seekers.
 *
 * After registration a job-seeker's profile is empty. Until the required
 * fields (name + location, the canonical location source of truth) are filled,
 * we redirect them to the profile page and block every other route. Employers,
 * admins and guests are never gated.
 */

const isJobSeeker = (user: User): boolean =>
  user.user_type === 'employee' || user.user_type === 'job_seeker';

export const isJobSeekerProfileComplete = (user: User | null): boolean => {
  if (!user || !isJobSeeker(user)) return true;
  return Boolean(
    user.first_name?.trim() &&
    user.last_name?.trim() &&
    user.profile?.location?.trim()
  );
};

// Routes a not-yet-onboarded job-seeker may still reach (profile itself, auth
// flows, and static/legal pages). Everything else redirects to the profile.
const ALLOWED_PATHS = [
  '/profile/employee',
  '/profile',
  '/login',
  '/register',
  '/forgot-password',
  '/reset-password',
  '/naudojimosi-salygos',
  '/kontaktai',
  '/pagalba',
];

const OnboardingGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isAuthenticated, isLoading } = useAuth();
  const { showToast } = useToast();
  const location = useLocation();

  const path = location.pathname;
  const allowed = ALLOWED_PATHS.some(p => path === p || path.startsWith(`${p}/`));
  // Wait until auth resolves before enforcing (avoids a flash / wrong bounce).
  const incomplete = !isLoading && isAuthenticated && !isJobSeekerProfileComplete(user);
  const blocked = incomplete && !allowed;

  // Tell the user *why* they were bounced, so a "dead" button makes sense.
  useEffect(() => {
    if (blocked) {
      showToast('Prašome užpildyti profilį, kad galėtumėte naudotis sistema.', 'info');
    }
  }, [blocked, path, showToast]);

  if (!blocked) {
    return <>{children}</>;
  }

  return <Navigate to="/profile/employee" replace />;
};

export default OnboardingGate;
